import type { ReactNode } from 'react';
import { DodoMark } from '../features/marketing/Logo';
import { Link } from 'react-router-dom';
import { ScreenNav } from './ScreenNav';

type Props = {
  title: string;
  audience: 'adult' | 'learner';
  children: ReactNode;
  // Where the logo/wordmark links. Defaults to each audience's signed-in
  // home (role-selection / learn) -- but a screen reachable before an
  // adult has a session (sign in, sign up, recovery, the magic-link
  // callback) must override this to "/", the public marketing page.
  // Left at the authenticated default there, the logo sent a
  // not-yet-signed-in visitor to a session-gated route, which bounced
  // them straight back to sign-in with no way back to the site at all.
  homeHref?: string;
  // Signed-in workspace screens that sit outside the sidebar shells (parent
  // Family access / Connect / Recommend, the teacher planning steps) pass
  // their dashboard here to get the shared Back + Dashboard row. Sign-in,
  // onboarding and other pre-dashboard screens leave it unset.
  dashboardHref?: string;
  backFallbackTo?: string;
  // Sign-in / sign-up / recovery / email-link screens: one centred column,
  // so the page title sits directly above the form card instead of far to
  // its left.
  narrow?: boolean;
};

export function PageFrame({
  title,
  audience,
  children,
  homeHref,
  dashboardHref,
  backFallbackTo,
  narrow = false,
}: Props) {
  // Learner screens (5-12 year olds) get their own calmer treatment: the
  // workspace forest palette + Plex Sans (.dm-learner in styles.css), a
  // centred card on a light paper background, and a smaller page heading so
  // the question itself -- not "Your activity" -- is the biggest thing on
  // the page. Adult screens (sign-in, role selection, parent Family access,
  // Connect, the teacher planning steps) share the workspace look via
  // .dm-workspace: same forest header, Plex Sans + Archivo Narrow titles.
  const learner = audience === 'learner';
  return (
    <div
      className={
        learner
          ? 'dm-learner flex min-h-screen flex-col bg-paper'
          : 'dm-workspace flex min-h-screen flex-col bg-paper'
      }
    >
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-3"
        href="#main-content"
      >
        Skip to main content
      </a>
      {/* Same header treatment as TeacherWorkspaceShell/ParentWorkspaceShell
          (dark forest bar, mint "D" badge, white wordmark) -- this
          component is the shared chrome for every screen that isn't part of
          the big dashboard shells (all learner screens, plus the simpler
          adult auth/parent flows), so it's the one place that previously
          looked like a different product. */}
      <header
        className={`flex shrink-0 items-center justify-between gap-2 px-4 print:hidden sm:px-10 ${
          learner ? 'h-16 bg-forest-950' : 'h-16 bg-forest-950 sm:h-[68px]'
        }`}
      >
        <Link
          to={
            homeHref ??
            (audience === 'adult' ? '/adult/role-selection' : '/learn')
          }
          className="flex min-w-0 items-center gap-2 sm:gap-3"
        >
          <DodoMark size={40} onDark className="shrink-0" />
          <span className="truncate text-2xl font-semibold text-white">
            DodoSkills
          </span>
        </Link>
        <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-mint">
          {audience === 'adult' ? 'Adult workspace' : 'Learning space'}
        </span>
      </header>
      <main
        id="main-content"
        className={
          learner
            ? 'mx-auto w-full max-w-xl flex-1 px-4 py-6 sm:py-12 [@media(orientation:landscape)_and_(max-height:900px)_and_(min-width:700px)]:py-4'
            : narrow
              ? 'mx-auto w-full max-w-md flex-1 px-4 py-10 sm:py-14'
              : 'mx-auto w-full max-w-5xl flex-1 px-4 py-10'
        }
        tabIndex={-1}
      >
        {learner ? (
          <div className="rounded-3xl border border-line bg-white p-5 shadow-card sm:p-10 [@media(orientation:landscape)_and_(max-height:900px)_and_(min-width:700px)]:px-10 [@media(orientation:landscape)_and_(max-height:900px)_and_(min-width:700px)]:py-6">
            <h1 className="text-xl font-semibold text-forest-950">{title}</h1>
            {children}
          </div>
        ) : (
          <>
            {dashboardHref && (
              <ScreenNav
                dashboardHref={dashboardHref}
                backFallbackTo={backFallbackTo}
              />
            )}
            {/* Narrow pages (sign-in) let the title run wider than their
                card, so it stays on one line on desktop. */}
            <h1
              className={`text-[30px] leading-tight font-bold text-forest-950 sm:text-[34px] ${
                narrow ? 'sm:-mx-24 sm:text-center' : ''
              }`}
            >
              {title}
            </h1>
            {children}
          </>
        )}
      </main>
      {/* No Terms/Privacy links here, since those pages currently only exist
          under the teacher-scoped /adult/teacher/* routes (a real,
          pre-existing gap, not something to paper over with a link into the
          wrong role's section). */}
      <footer
        className={`px-4 text-center text-sm font-semibold print:hidden sm:px-10 ${
          learner ? 'py-5 text-muted' : 'border-t border-line py-6 text-muted'
        }`}
      >
        DodoSkills © 2026
      </footer>
    </div>
  );
}
